import React, {useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import {Tooltip} from "@mui/material";
import {Link} from "react-router-dom";
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner,
} from "material-react-table";
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import ButtonCreate from "../../../components/UI/button/ButtonCreate";
import ModalModule from "../../../components/UI/modal/ModalModule";
import CompositionController from "../../../API/organization/CompositionController";
import {SideBaseModule} from "../../../data/SideBaseModule";
import {HeightLocationModule} from "../../../data/HeightLocationModule";
import {flattenModuleCompositionRow} from "../../../utils/flattenModuleCompositionRow";
import {withCompositionIdQuery} from "../../../utils/withCompositionIdQuery";

const EMPTY_MODAL_INIT = {}

const NAME_COL_WIDTH = 400

const HEADER_COL_WIDTHS = {
    name: NAME_COL_WIDTH,
    sideBaseModule: 210,
    heightLocation: 340,
    width: 160,
    height: 150,
    depth: 170,
}

const TABLE_WIDTH = Object.values(HEADER_COL_WIDTHS).reduce((sum, width) => sum + width, 0)

const headerFitColSx = (width) => ({
    width,
    minWidth: width,
    maxWidth: width,
    flex: `0 0 ${width}px`,
    boxSizing: 'border-box',
})

const columnSx = (columnId) => headerFitColSx(HEADER_COL_WIDTHS[columnId] ?? 96)

const NameCell = ({to, children}) => {
    const textRef = useRef(null)
    const [showTooltip, setShowTooltip] = useState(false)
    const label = children == null ? "" : String(children)

    const handleMouseEnter = () => {
        const el = textRef.current
        setShowTooltip(Boolean(el && el.scrollWidth > el.clientWidth))
    }

    return (
        <Tooltip
            arrow
            placement="top"
            title={label}
            disableHoverListener={!showTooltip}
        >
            <Box
                component={Link}
                to={to}
                ref={textRef}
                onMouseEnter={handleMouseEnter}
                sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                }}
            >
                {label}
            </Box>
        </Tooltip>
    )
}

const DependentModulesTable = ({
                                   data,
                                   setData,
                                   dependentModuleCompositionId,
                                   compositionId,
                                   compositionName,
                                   handleModalAlert,
                               }) => {
    const rows = useMemo(
        () => (Array.isArray(data) ? data : []),
        [data],
    )
    const [modal, setModal] = useState(false)
    const [loading, setLoading] = useState(false)

    const columns = useMemo(() => [
        {
            id: 'name',
            header: 'Наименование',
            accessorKey: 'name',
            grow: false,
            size: NAME_COL_WIDTH,
            minSize: NAME_COL_WIDTH,
            maxSize: NAME_COL_WIDTH,
            Cell: ({row, renderedCellValue}) => (
                <NameCell to={withCompositionIdQuery(
                    '/organization/module-composition/' + row.original.id,
                    compositionId,
                    compositionName,
                )}>
                    {renderedCellValue}
                </NameCell>
            ),
        },
        {
            id: 'sideBaseModule',
            header: 'Расположение',
            accessorFn: (row) => SideBaseModule[row.sideBaseModule] ?? '',
            grow: false,
            size: HEADER_COL_WIDTHS.sideBaseModule,
            minSize: HEADER_COL_WIDTHS.sideBaseModule,
            maxSize: HEADER_COL_WIDTHS.sideBaseModule,
        },
        {
            id: 'heightLocation',
            header: 'Расположение (по высоте)',
            accessorFn: (row) => HeightLocationModule[row.heightLocation] ?? '',
            grow: false,
            size: HEADER_COL_WIDTHS.heightLocation,
            minSize: HEADER_COL_WIDTHS.heightLocation,
            maxSize: HEADER_COL_WIDTHS.heightLocation,
        },
        {
            id: 'width',
            header: 'Ширина',
            accessorKey: 'width',
            grow: false,
            size: HEADER_COL_WIDTHS.width,
            minSize: HEADER_COL_WIDTHS.width,
            maxSize: HEADER_COL_WIDTHS.width,
        },
        {
            id: 'height',
            header: 'Высота',
            accessorKey: 'height',
            grow: false,
            size: HEADER_COL_WIDTHS.height,
            minSize: HEADER_COL_WIDTHS.height,
            maxSize: HEADER_COL_WIDTHS.height,
        },
        {
            id: 'depth',
            header: 'Глубина',
            accessorKey: 'depth',
            grow: false,
            size: HEADER_COL_WIDTHS.depth,
            minSize: HEADER_COL_WIDTHS.depth,
            maxSize: HEADER_COL_WIDTHS.depth,
        },
    ], [compositionId, compositionName])

    const handleClose = () => {
        setModal(false)
    }

    const handleSubmitDepend = (moduleCompositionId, submit) => {
        setLoading(true)
        CompositionController.addDependentModuleComposition(dependentModuleCompositionId, moduleCompositionId)
            .then(function (response) {
                const row = flattenModuleCompositionRow(response?.data)
                const newData = row?.id == null ? data : [...data, row]
                setData(newData)
                submit()
                handleModalAlert("Данные добавлены", 'success')
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
            .finally(() => setLoading(false))
    }

    return (
        <Box sx={{overflow: 'auto', width: '100%'}}>
            <Box sx={{width: TABLE_WIDTH}}>
                <Box sx={{display: 'flex', justifyContent: "flex-end", paddingBottom: "30px"}}>
                    <ButtonCreate
                        text={"Добавить"}
                        width={160}
                        onClick={() => setModal(true)}
                    />
                </Box>
                <MaterialReactTable
                renderTopToolbar={({table}) => (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "7px",
                        width: TABLE_WIDTH,
                        boxSizing: 'border-box',
                    }}>
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: "flex"}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                )}
                muiTablePaperProps={{
                    sx: {
                        width: 'fit-content',
                        maxWidth: 'fit-content',
                    },
                }}
                muiTableContainerProps={{
                    sx: {
                        width: TABLE_WIDTH,
                        maxWidth: TABLE_WIDTH,
                        maxHeight: `calc(100vh - 420px)`,
                    },
                }}
                muiTableProps={{
                    sx: {
                        width: TABLE_WIDTH,
                        tableLayout: 'fixed',
                    },
                }}
                muiTableBodyCellProps={({column}) => ({
                    sx: {
                        p: '0px 0px 0px 10px',
                        m: 0,
                        border: "none",
                        "&:after": {display: "none"},
                        "&:not(:last-of-type)": {borderRight: '1px solid #EAEBEA'},
                        "& a": {
                            color: "#006CCC",
                            textDecoration: "none",
                            "&:hover": {textDecoration: "underline"},
                        },
                        ...columnSx(column.id),
                    },
                })}
                muiTableHeadCellProps={({column}) => ({
                    sx: {
                        border: '1px solid #EAEBEA',
                        backgroundColor: "#F5F6FA",
                        padding: "10px",
                        paddingBottom: "0.2rem",
                        paddingTop: "0.2rem",
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                        overflow: 'visible',
                        '& .Mui-TableHeadCell-Content': {
                            flexWrap: 'nowrap',
                        },
                        '& .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
                            overflow: 'visible',
                            textOverflow: 'clip',
                            whiteSpace: 'nowrap',
                            maxWidth: 'none',
                        },
                        ...columnSx(column.id),
                    },
                })}
                muiTableBodyRowProps={() => ({
                    sx: (theme) => ({
                        height: '36px',
                        backgroundColor: "white",
                        "&:hover": {backgroundColor: theme.palette.primary.stroke},
                        '&:nth-of-type(even)': {
                            backgroundColor: theme.palette.primary.lightGrey,
                            "&:hover": {backgroundColor: theme.palette.primary.stroke},
                        },
                    }),
                })}
                columns={columns}
                data={rows}
                getRowId={(originalRow) => String(originalRow.id)}
                enableRowActions={false}
                enableColumnActions={false}
                enableToolbarInternalActions={false}
                initialState={{
                    showGlobalFilter: true,
                    density: "compact",
                    pagination: {pageIndex: 0, pageSize: 100},
                }}
                localization={MRT_Localization_RU}
            />
            </Box>
            {modal && <ModalModule
                title={"Добавить"}
                open={modal}
                onClose={handleClose}
                handleSubmit={handleSubmitDepend}
                initValue={EMPTY_MODAL_INIT}
                loading={loading}
                dependentModuleCompositionId={dependentModuleCompositionId}
                compositionId={compositionId}
                handleModalAlert={handleModalAlert}
            />}
        </Box>
    )
}

export default DependentModulesTable
